import { Integration } from '@/types/generated/graphql';
import { cache } from '@/container/apollo-client/cache';
import { Reference, StoreObject } from '@apollo/client';

export function cacheIncrementIntegrationCount(mutationData: Integration | null | undefined) {
  const status = mutationData?.integrationUser?.status;
  if (status !== 'active') return;
  cache.modify({
    fields: {
      integrationCount(existing = { data: [] }) {
        const updatedData = existing?.data?.map((metric: { key: string; value: number }) => {
          if (metric?.key === 'activeCount') {
            return {
              ...metric,
              value: metric?.value + 1,
            };
          }
          return metric;
        });

        return {
          ...existing,
          data: updatedData,
        };
      },
    },
  });
}

export function cacheDecrementIntegrationCount(successMessage: string | null | undefined) {
  if (!successMessage) return;
  cache.modify({
    fields: {
      integrationCount(existing = { data: [] }) {
        const updatedData = existing?.data?.map((metric: { key: string; value: number }) => {
          if (metric?.key === 'activeCount') {
            return {
              ...metric,
              value: metric?.value - 1,
            };
          }
          return metric;
        });
        return {
          ...existing,
          data: updatedData,
        };
      },
    },
  });
}

export function cacheRemoveIntegration(integrationId: number | null | undefined) {
  if (!integrationId) return;
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      integrationsV2(existingData, { storeFieldName, readField }) {
        const variables = {
          includeInactive: false,
          query: '',
        };
        const expectedFieldName = `integrationsV2:${JSON.stringify(variables)}`;
        if (storeFieldName === expectedFieldName && existingData) {
          return {
            ...existingData,
            data: existingData?.data?.filter((integrationRef: Reference | StoreObject | undefined) => {
              const id = readField('id', integrationRef);
              return id !== integrationId;
            }),
          };
        }
        // Return unchanged for other queries
        return existingData;
      },
    },
  });
}

export function cacheAddIntegration(integrationId: number | null | undefined) {
  if (!integrationId) return;
  const variables = {
    includeInactive: false,
    query: '',
  };
  const expectedFieldName = `integrationsV2:${JSON.stringify(variables)}`;
  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      integrationsV2(existingData = {}, { storeFieldName, readField, toReference }) {
        if (storeFieldName !== expectedFieldName) return existingData;
        const newRef = toReference({ __typename: 'Integration', id: integrationId });
        const exists = existingData.data?.some(
          (ref: Reference | StoreObject | undefined) => readField('id', ref) === integrationId
        );
        if (exists) return existingData;
        return {
          ...existingData,
          data: [...(existingData.data ?? []), newRef],
        };
      },
    },
  });
}
